// The ambient sky (DESIGN-WL-AMBIENT-SKIN-BACKDROP-1, HOME_AMBIENT_SKIN_SPEC §2).
//
// A decorative frame under the page stage: fixed, inset 0, no pointer events,
// hidden from assistive tech. The dots are a 1-bit mask (files/ambient/<sky>.png,
// through the files plane); the layers paint the brand ink `stageInk` and the
// mask decides where it shows.
//
// THE DRIFT (DESIGN-WL-BOOT-LOADER-MOTION-1, Nika 2026-09-28 "make clouds
// animation too"). The frame itself stays still and carries the FADE (the
// horizon never moves); the art rides on layers inside it. A sky whose mask
// tiles without a seam (`ambientSkyDrifts` — the clouds) gets two layers
// twice the viewport wide that repeat the tile across and slide left by one
// tile per loop: SkyNear at the dashboard's own scale (one tile per viewport
// width, 90 s) and SkyFar at half the scale and half the ink, slower (150 s),
// for depth. Only `transform` moves (the `ambientDrift` keyframes) — no mask
// position, no layout. Every other sky is one still layer (SkyStill), exactly
// the art the horizon always showed. Reduced motion stands the drift still:
// the OS query here (`@reducedMotion`), the app's own Settings choice in the
// shell sheet (<html data-motion="reduced">).
//
// THE SKIN is never read here (the kit's skin contract: a component never
// branches on the skin). The frame is display none; the SHELL shows it — one
// rule in app.js's injected sheet, beside the skin stamp, for the ambient
// skin only. No stamp or the solid skin = no sky, and a sheet that never
// loaded fails closed. On top of that the sky's own off switches (hide:
// inline display none, beats the shell rule) — the record's sky is 'none',
// prefers-reduced-transparency matches, the workspace paints its own ground,
// the record has not arrived, or the sky has no files-plane record — live in
// functions/ambientBackdrop.js (`ambientSkyState`).
//
// Placement (the frame's fade): 'horizon' (default — Nika 2026-09-27 "move
// clouds illustration at bottom") caps the art to the lower band of the
// viewport and fades it out upward, so the header, greeting and App strip sit
// on clean ground (functions/ambientBackdrop.js ambientFadeMask has the
// numbers). 'top' (the shell's mount — DESIGN-WL-HOME-SKY-TOP-GROUND-1, Nika
// 2026-10-01 "maybe we move moving clouds at the top side … clouds and
// moutain should be the default") is the SAME horizon band on a frame
// mirrored vertically (`ambientSkyFlip`): the cloud mass hangs from the top
// edge and fades out downward; the bottom belongs to the ground
// (AmbientGround), which rises at the end of the home scroll. Its band stops
// at the CLEAR EDGE — the top of the `fadeAnchor` (the greeting's summary),
// measured — so the summary sits on clean ground, and on the home it slides
// away with the page top (the shell sheet). The person can keep the bottom
// band instead (Personalise → Background, `homeLook.skyLayout: 'bottom'`):
// `ambientSkyPlacement` then answers 'horizon', today's geometry exactly, and
// the ground stays off. 'none' is the whole art with no fade;
// 'grid' fades the sky out at the top edge of the `fadeAnchor` node (the home
// grid), measured on render and on window resize.
//
// Stacking: zIndex -1 paints the frame above its parent's own background and
// below every in-flow child — so the MOUNT parent must be a stacking context
// and the grounds between it and the page content must not repaint the stage
// over the sky (docs/patches/ambient-mount.app.js.patch).

export const AmbientBackdrop = {
  fadeTo: 'horizon',
  fadeAnchor: 'GridContainer',
  state: { fadeTop: null, skyAway: false },

  aria: { hidden: 'true' },
  // The EFFECTIVE placement (`ambientSkyPlacement`: 'top' turns back into
  // 'horizon' when the person chose the bottom layout) for the shell sheet's
  // scroll-away rule, and the fallback's "the home left its top" flag.
  attr: {
    'data-sky-layout': (el, s) => el.call('ambientSkyPlacement', el.fadeTo, s),
    'data-sky-away': (el, s) => (s.skyAway ? 'true' : null)
  },
  display: 'none',
  position: 'fixed',
  inset: '0',
  zIndex: '-1',
  pointerEvents: 'none',
  overflow: 'hidden',

  hide: (el, s) => !el.call('ambientSkyState', s).enabled,
  // The fade belongs to the still frame, so the horizon stays put while the
  // art drifts under it.
  maskImage: (el, s) => {
    const placement = el.call('ambientSkyPlacement', el.fadeTo, s)
    const measured = placement === 'grid' || placement === 'top'
    return el.call('ambientFadeMask', measured ? s.fadeTop : null, placement)
  },
  maskSize: '100% 100%',
  maskRepeat: 'no-repeat',
  // 'top' mirrors the frame (fade and art together) onto the top edge; the
  // drift layers keep their own translateX.
  transform: (el, s) => el.call('ambientSkyFlip', el.call('ambientSkyPlacement', el.fadeTo, s)),

  // The art layers. Each is the stageInk masked by the sky's art on the
  // bottom edge: the ink is STATIC (a class rule, so a per-skin
  // `--color-stageInk` override reaches it), the art URL reactive (inline),
  // the drift static too (class rules the @reducedMotion block can win over).
  // Every factory stands alone (frank serializes each one by itself).
  SkyFar: {
    hide: (el, s) => !el.call('ambientSkyDrifts', el.call('ambientSkyState', s).sky),
    position: 'absolute',
    left: '0',
    bottom: '0',
    height: '100%',
    pointerEvents: 'none',
    background: 'stageInk',
    maskImage: (el, s) => el.call('ambientMaskImage', el.call('ambientSkyState', s).url),
    width: '200%',
    opacity: '0.5',
    maskSize: '25% auto',
    maskRepeat: 'repeat-x',
    maskPosition: 'left bottom',
    animation: 'ambientDrift',
    animationDuration: '150s',
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
    '@reducedMotion': { animation: 'none' }
  },
  SkyNear: {
    hide: (el, s) => !el.call('ambientSkyDrifts', el.call('ambientSkyState', s).sky),
    position: 'absolute',
    left: '0',
    bottom: '0',
    height: '100%',
    pointerEvents: 'none',
    background: 'stageInk',
    maskImage: (el, s) => el.call('ambientMaskImage', el.call('ambientSkyState', s).url),
    width: '200%',
    maskSize: '50% auto',
    maskRepeat: 'repeat-x',
    maskPosition: 'left bottom',
    animation: 'ambientDrift',
    animationDuration: '90s',
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
    '@reducedMotion': { animation: 'none' }
  },
  SkyStill: {
    hide: (el, s) => !!el.call('ambientSkyDrifts', el.call('ambientSkyState', s).sky),
    position: 'absolute',
    left: '0',
    bottom: '0',
    height: '100%',
    pointerEvents: 'none',
    background: 'stageInk',
    maskImage: (el, s) => el.call('ambientMaskImage', el.call('ambientSkyState', s).url),
    width: '100%',
    maskSize: '100% auto',
    maskRepeat: 'no-repeat',
    maskPosition: 'center bottom'
  },

  onRender: (el, s) => el.call('ambientScheduleFade', el, s),
  onWindowResize: (e, el, s) => el.call('ambientScheduleFade', el, s),
  onRemove: (el) => el.call('ambientCancelFade', el)
}
